/**
 * Broker Partner Repository
 * Handles partner/broker-related API calls
 */

import axios, { AxiosInstance } from 'axios'
import API_CONFIG from '../config/api.config'
import authRepository from './auth.repository'

export interface PartnerBrand {
  id: number
  fullLogo: string | null
  responsiveLogo: string | null
}

export interface Partner {
  id: number
  shortName: string
  emailDomain: string | null
  fullName: string
  mainColor: string | null
  responsiveLogoUrl: string | null
  fullLogoUrl: string | null
  websiteUrl: string | null
  instagramProfileUrl: string | null
  facebookProfileUrl: string | null
  twitterProfileUrl: string | null
  proExpirationDate: string | null
  partnerBrand: PartnerBrand | null
}

export interface Broker {
  id: number
  name: string
  integrated: boolean | null
  logoUrl: string | null
  downloadUrl: string | null
  partner: Partner
  iburl: string | null
}

class PartnerRepository {
  private api: AxiosInstance

  constructor() {
    this.api = axios.create({
      timeout: API_CONFIG.TIMEOUT,
      headers: {
        'Content-Type': 'application/json'
      }
    })
  }

  /**
   * Get brokers by partner ID
   * Returns array of brokers associated with the partner
   */
  async getBrokersByPartner(partnerId: number): Promise<Broker[]> {
    try {
      const response = await this.api.get<Broker[]>(
        API_CONFIG.PARTNER.BROKERS(partnerId),
        {
          headers: authRepository.getAuthHeader()
        }
      )

      return response.data
    } catch (error: any) {
      console.error('Failed to get brokers by partner:', error)
      throw error
    }
  }

  /**
   * Get the first broker info for a partner
   * Convenience method that returns the first broker from the array
   */
  async getBrokerInfo(partnerId: number): Promise<Broker | null> {
    try {
      const brokers = await this.getBrokersByPartner(partnerId)
      return brokers.length > 0 ? brokers[0] : null
    } catch (error) {
      console.error('Failed to get broker info:', error)
      return null
    }
  }

  /**
   * Partial edit of the partner's branding/company details (SFB-1239).
   * Multipart contract: JSON part `data` ({shortName, fullName, mainColor,
   * websiteUrl}) plus optional `responsiveImage` / `fullImage` file parts.
   * Fields not sent are preserved server-side. Requires the caller to be a
   * MANAGER/OPERATIONS staff member of the partner.
   */
  async updatePartnerDetails(
    partnerId: number,
    data: { shortName?: string, fullName?: string, mainColor?: string | null, websiteUrl?: string | null },
    responsiveImage?: File | null,
    fullImage?: File | null
  ): Promise<Partner> {
    const formData = new FormData()
    formData.append('data', new Blob([JSON.stringify(data)], { type: 'application/json' }))
    if (responsiveImage) {
      formData.append('responsiveImage', responsiveImage)
    }
    if (fullImage) {
      formData.append('fullImage', fullImage)
    }
    // Don't set Content-Type: axios adds the multipart boundary itself.
    const response = await this.api.put<Partner>(
      API_CONFIG.PARTNER.DETAILS(partnerId),
      formData,
      { headers: { ...authRepository.getAuthHeader(), 'Content-Type': undefined } as any }
    )
    return response.data
  }
}

export default new PartnerRepository()
